import { useState } from "react";
import { toast } from "sonner";
import { X, Users, Copy, Plus, LogIn, LogOut, UserMinus, RefreshCw, Crown } from "lucide-react";
import api, { errMsg } from "@/lib/api";

export default function GroupsDialog({ open, onClose, groups, onChange, userId }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");

  if (!open) return null;

  const run = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const create = () => name.trim() && run(async () => { await api.post("/groups", { name: name.trim() }); setName(""); }, "Η ομάδα δημιουργήθηκε");
  const join = () => code.trim() && run(async () => { await api.post("/groups/join", { code: code.trim() }); setCode(""); }, "Μπήκατε στην ομάδα");
  const leave = (g) => {
    if (!window.confirm(`Αποχώρηση από την ομάδα «${g.name}»; Οι υποθέσεις σας που μοιράζεστε εκεί θα γίνουν ιδιωτικές.`)) return;
    run(() => api.post(`/groups/${g.id}/leave`), "Αποχωρήσατε από την ομάδα");
  };
  const remove = (g, m) => {
    if (!window.confirm(`Αφαίρεση του/της ${m.name} από την ομάδα;`)) return;
    run(() => api.delete(`/groups/${g.id}/members/${m.id}`), "Το μέλος αφαιρέθηκε");
  };
  const rotate = (g) => {
    if (!window.confirm("Θα δημιουργηθεί νέος κωδικός πρόσκλησης και ο παλιός θα πάψει να ισχύει. Τα τρέχοντα μέλη δεν επηρεάζονται. Συνέχεια;")) return;
    run(() => api.post(`/groups/${g.id}/rotate-code`), "Δημιουργήθηκε νέος κωδικός");
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid="groups-dialog"
      style={{ background: "rgba(245,245,247,0.6)", backdropFilter: "saturate(180%) blur(20px)", WebkitBackdropFilter: "saturate(180%) blur(20px)" }} onClick={onClose}>
      <div className="card w-full max-w-md max-h-[92vh] overflow-y-auto p-6 fadeup" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Users className="w-5 h-5 text-[var(--txt-2)]" />Ομάδες / Γραφείο</h3>
          <button onClick={onClose} className="btn-ghost w-9 h-9 grid place-items-center"><X className="w-4 h-4" /></button>
        </div>

        <p className="text-sm text-[var(--txt-2)] mb-4">Οι υποθέσεις που μοιράζεστε σε μια ομάδα είναι ορατές σε όλα τα μέλη της ταυτόχρονα. Τα στοιχεία επικοινωνίας δεν μοιράζονται ποτέ.</p>

        <div className="space-y-3 mb-5">
          {groups.length === 0 && <div className="text-sm text-[var(--txt-3)]">Δεν ανήκετε σε καμία ομάδα ακόμη.</div>}
          {groups.map((g) => (
            <div key={g.id} data-testid={`group-row-${g.id}`} className="elev rounded-xl px-3 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">{g.name}</span>
                <div className="flex items-center gap-1.5">
                  {g.is_owner && g.code && (
                    <button data-testid={`copy-code-${g.id}`} onClick={() => { navigator.clipboard?.writeText(g.code); toast.success("Αντιγράφηκε ο κωδικός"); }}
                      title="Κωδικός πρόσκλησης" className="flex items-center gap-1.5 text-xs font-mono btn-ghost px-2.5 py-1">
                      {g.code} <Copy className="w-3 h-3" />
                    </button>
                  )}
                  {g.is_owner && (
                    <button data-testid={`rotate-code-${g.id}`} onClick={() => rotate(g)} title="Νέος κωδικός πρόσκλησης" className="btn-ghost w-7 h-7 grid place-items-center">
                      <RefreshCw className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <ul className="mt-2 space-y-1" data-testid={`members-${g.id}`}>
                {(g.members || []).map((m) => (
                  <li key={m.id} className="flex items-center justify-between text-xs text-[var(--txt-2)]">
                    <span className="flex items-center gap-1.5">
                      {m.is_owner && <Crown className="w-3 h-3 text-amber-600" />}{m.name}{m.id === userId ? " (εσείς)" : ""}
                    </span>
                    {g.is_owner && m.id !== userId && (
                      <button data-testid={`remove-member-${m.id}`} onClick={() => remove(g, m)} title="Αφαίρεση μέλους" className="btn-ghost w-6 h-6 grid place-items-center hover:text-rose-600">
                        <UserMinus className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <button data-testid={`leave-group-${g.id}`} onClick={() => leave(g)} className="mt-2 text-xs text-[var(--txt-2)] underline flex items-center gap-1">
                <LogOut className="w-3 h-3" />Αποχώρηση από την ομάδα
              </button>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div>
            <label className="label block mb-1.5">Δημιουργία νέας ομάδας</label>
            <div className="flex gap-2">
              <input data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)}
                className="field flex-1 px-3 py-2.5 text-sm" placeholder="π.χ. Δικηγορικό Γραφείο Α" />
              <button data-testid="create-group-btn" onClick={create} className="btn-primary px-4 flex items-center gap-1.5 text-sm"><Plus className="w-4 h-4" />Δημιουργία</button>
            </div>
          </div>
          <div>
            <label className="label block mb-1.5">Είσοδος με κωδικό</label>
            <div className="flex gap-2">
              <input data-testid="group-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="field flex-1 px-3 py-2.5 text-sm font-mono" placeholder="π.χ. K7M2XQ9PA4" maxLength={32} />
              <button data-testid="join-group-btn" onClick={join} className="btn-ghost px-4 flex items-center gap-1.5 text-sm"><LogIn className="w-4 h-4" />Είσοδος</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
